import { referenceService } from "src/api/references/referenceService";
import {
  GetConnectionOfMedCardParamsDto,
  GetCardTableDataParamsDto,
} from "src/api/references/referenceServicesDtos";
import { getCardData } from "./cardData";

interface FetchDependentCardDataParams {
  cardType: string;
  patientId?: number;
  orId?: number;
  recordId?: number;
  includeMcpDetails?: boolean;
  masterColumnName?: string;
}

interface FetchDependentCardDataResult {
  masterRecordId?: number;
  comId?: number;
  masterRecord?: any;
  detailRecord?: any;
}

const inflightRequests = new Map<string, Promise<FetchDependentCardDataResult | null>>();
const requestCache = new Map<string, { result: FetchDependentCardDataResult | null, time: number }>();
const CACHE_TTL = 30000; // 30 seconds

export const fetchDependentCardData = async ({
  cardType,
  patientId,
  orId,
  recordId,
  includeMcpDetails = true,
  masterColumnName = "COM_ID",
}: FetchDependentCardDataParams): Promise<FetchDependentCardDataResult | null> => {
  const cacheKey = `${cardType}-${patientId}-${orId}-${recordId}-${includeMcpDetails}-${masterColumnName}`;

  if (requestCache.has(cacheKey)) {
    const cached = requestCache.get(cacheKey)!;
    if (Date.now() - cached.time < CACHE_TTL) {
      return cached.result;
    }
    requestCache.delete(cacheKey);
  }

  if (inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey)!;
  }

  const performFetch = async (): Promise<FetchDependentCardDataResult | null> => {
    try {
      const cardConfig = getCardData(cardType);
      if (!cardConfig) {
        throw new Error(`Card data not found for type: ${cardType}`);
      }

      let detailColumnValue: number | null =
        typeof recordId !== "undefined" && recordId !== null
          ? Number(recordId)
          : null;
      let masterRecord: any = null;
      let comIdValue: number | undefined;

      if (!detailColumnValue && cardConfig.columnValue && cardConfig.columnValue !== 0) {
        detailColumnValue = cardConfig.columnValue;
      }

      if (!detailColumnValue && patientId && orId) {
        const connectionsResponse =
          await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
            pt: patientId,
            orId,
          } as GetConnectionOfMedCardParamsDto);

        const connections = connectionsResponse.data || [];
        if (!connections.length) {
          return null;
        }

        const latestConnection = [...connections].sort(
          (a, b) => (b.comid ?? 0) - (a.comid ?? 0)
        )[0];
        comIdValue = latestConnection?.comid;

        if (!comIdValue) {
          return null;
        }

        if (!cardConfig.masterTableName) {
          return null;
        }

        const masterResponse = await referenceService.card.getCardTableData({
          tableName: cardConfig.masterTableName,
          columnName: masterColumnName,
          columnValue: comIdValue,
        } as GetCardTableDataParamsDto);

        if (!masterResponse.data?.length) {
          return null;
        }

        masterRecord = masterResponse.data.sort(
          (a: any, b: any) =>
            new Date(b.PROCESS_TIME || 0).getTime() -
            new Date(a.PROCESS_TIME || 0).getTime()
        )[0];

        detailColumnValue = masterRecord?.ID ?? null;
      }

      if (!detailColumnValue) {
        return null;
      }

      const detailResponse = await referenceService.card.getCardTableData({
        tableName: cardConfig.tableName,
        columnName: cardConfig.columnName,
        columnValue: detailColumnValue,
        mcpdNameList: includeMcpDetails
          ? Object.keys(cardConfig.mcpConfig || {}).join(",")
          : undefined,
      } as GetCardTableDataParamsDto);

      const detailRecord = detailResponse.data?.sort(
        (a: any, b: any) =>
          new Date(b.PROCESS_TIME || 0).getTime() -
          new Date(a.PROCESS_TIME || 0).getTime()
      )[0];

      const result = {
        masterRecordId: detailColumnValue,
        comId: comIdValue,
        masterRecord,
        detailRecord,
      };

      requestCache.set(cacheKey, { result, time: Date.now() });
      return result;
    } catch (error) {
      console.error("fetchDependentCardData error:", error);
      return null;
    } finally {
      inflightRequests.delete(cacheKey);
    }
  };

  const requestPromise = performFetch();
  inflightRequests.set(cacheKey, requestPromise);
  return requestPromise;
};
